import type { Platform, ScriptConfig } from "./types";

const VALID_PLATFORMS: ReadonlySet<Platform> = new Set(["vtex", "tray", "shopware", "unknown"]);

function toPlatform(value: string | null): Platform {
  if (value && VALID_PLATFORMS.has(value as Platform)) return value as Platform;
  return "unknown";
}

function toBool(value: string | null, fallback: boolean): boolean {
  if (value === null) return fallback;
  return value !== "false";
}

function readSrcParam(el: HTMLScriptElement | null, name: string): string | null {
  if (!el) return null;

  try {
    return new URL(el.src).searchParams.get(name);
  } catch {
    return null;
  }
}

// Snippet antigo: data-public-key="pk_...". Snippet novo: src="...script.js?publicKey=pk_..."
// — tag managers costumam remover atributos data-* do script, a query string sobrevive.
// O atributo continua tendo prioridade para não quebrar quem já instalou.
function readPublicKey(el: HTMLScriptElement | null): string {
  if (el?.dataset.publicKey) return el.dataset.publicKey;
  return readSrcParam(el, "publicKey") ?? "";
}

// Só id inteiro positivo: qualquer outra coisa vira null (principal), em vez de mandar lixo pro backend.
const CONNECTION_ID_PATTERN = /^[1-9]\d{0,9}$/;

function readConnectionId(el: HTMLScriptElement | null): string | null {
  const value = el?.dataset.connectionId ?? readSrcParam(el, "connectionId");
  return value && CONNECTION_ID_PATTERN.test(value) ? value : null;
}

// Mesmo motivo do publicKey: tag manager remove os data-* do script, então a plataforma
// também precisa vir na query (?platform=shopware). data-platform continua tendo prioridade.
function readPlatform(el: HTMLScriptElement | null): Platform {
  return toPlatform(el?.dataset.platform ?? readSrcParam(el, "platform"));
}

export function readConfig(currentScript: HTMLOrSVGScriptElement | null): ScriptConfig {
  const el = currentScript as HTMLScriptElement | null;

  return {
    publicKey: readPublicKey(el),
    connectionId: readConnectionId(el),
    platform: readPlatform(el),
    apiBase: el?.dataset.apiBase ?? "https://api.v3.indexaai.com",
    features: {
      tracking: toBool(el?.dataset.tracking ?? null, true),
      schema: toBool(el?.dataset.schema ?? null, true),
      chatbot: toBool(el?.dataset.chatbot ?? null, true),
    },
  };
}

export function getCurrentScript(): HTMLOrSVGScriptElement | null {
  if (document.currentScript) return document.currentScript;
  const scripts = document.querySelectorAll<HTMLScriptElement>(
    'script[data-public-key], script[src*="publicKey="]',
  );
  return scripts.item(scripts.length - 1) ?? null;
}
